import React from "react";
import { Target, Eye, Heart, Award, Building2, Users2 } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";

const VALUE_ICONS = [Target, Users2, Award];
const TEAM_ICONS = [Building2, Users2, Heart, Award];

export default function About() {
  const t = useT();
  const values = (t.about.values || []).map((v, i) => ({ ...v, icon: VALUE_ICONS[i] }));
  const teamCards = (t.about.teamCards || []).map((c, i) => ({ ...c, icon: TEAM_ICONS[i] }));

  return (
    <>
      <section className="relative bg-delfi-dark text-white pt-36 pb-24 overflow-hidden" data-testid="about-hero">
        <div className="chevron-bg right-[-8%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="delfi-container relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> {t.about.eyebrow}
          </div>
          <h1 className="headline-display text-white mt-4 text-[clamp(2.2rem,5vw,4rem)]" data-testid="about-title">
            {t.about.titleLead} <span className="text-delfi-red">{t.about.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">{t.about.description}</p>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="about-mission">
        <div className="delfi-container grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div className="delfi-card h-full">
              <div className="w-12 h-12 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center mb-5">
                <Target size={22} />
              </div>
              <h3 className="text-2xl font-bold text-delfi-navy">{t.about.missionTitle}</h3>
              <p className="mt-3 text-delfi-navy/75 leading-relaxed">{t.about.missionBody}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="delfi-card h-full">
              <div className="w-12 h-12 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center mb-5">
                <Eye size={22} />
              </div>
              <h3 className="text-2xl font-bold text-delfi-navy">{t.about.visionTitle}</h3>
              <p className="mt-3 text-delfi-navy/75 leading-relaxed">{t.about.visionBody}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-delfi-cloud py-24" data-testid="about-values">
        <div className="delfi-container">
          <Reveal>
            <SectionHeading
              eyebrow={t.about.valuesEyebrow}
              title={t.about.valuesTitle}
              description={t.about.valuesDescription}
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="delfi-card h-full">
                    <div className="w-12 h-12 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center mb-5">
                      <Icon size={22} />
                    </div>
                    <h3 className="text-lg font-semibold text-delfi-navy mb-2">{v.title}</h3>
                    <p className="text-delfi-navy/70 text-sm leading-relaxed">{v.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-delfi-navy text-white py-24 relative overflow-hidden" data-testid="about-timeline">
        <div className="chevron-bg left-[-16%] top-[-30%] opacity-[0.06]" aria-hidden="true">›</div>
        <div className="delfi-container relative">
          <Reveal>
            <SectionHeading
              dark
              eyebrow={t.about.journeyEyebrow}
              title={t.about.journeyTitle}
              description={t.about.journeyDescription}
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="about-team">
        <div className="delfi-container grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
              <Chevron size={14} /> {t.about.teamEyebrow}
            </div>
            <h2 className="headline-section text-3xl md:text-5xl mt-4 text-delfi-navy">
              {t.about.teamTitleLead} <span className="text-delfi-red">{t.about.teamTitleHighlight}</span>
            </h2>
            <p className="mt-6 text-delfi-navy/75 text-lg leading-relaxed">{t.about.teamBody1}</p>
            <p className="mt-4 text-delfi-navy/75 text-lg leading-relaxed">{t.about.teamBody2}</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              {teamCards.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="delfi-card">
                    <div className="w-10 h-10 rounded-lg bg-delfi-red/10 text-delfi-red flex items-center justify-center mb-4">
                      <Icon size={18} />
                    </div>
                    <p className="text-2xl font-extrabold text-delfi-navy tracking-tight">{item.stat}</p>
                    <p className="text-sm text-delfi-navy/65 mt-1">{item.sub}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner title={t.about.ctaTitle} subtitle={t.about.ctaSubtitle} />
    </>
  );
}
